<template>
  <div>
    <panel class="invoices-repository">
      <template slot="toolbar">
        <w-input class="mr-3 flex-grow-1" :model="$schema.number" />

        <w-datepicker class="mr-3 flex-grow-1" :model="$schema.date" />

        <w-input class="mr-3 flex-grow-1" mask="##########" :model="$schema.nip" />

        <w-button class="btn-primary mt-4" text="Filtruj" icon="fas fa-search" @click="applyFilters" />
      </template>

      <div class="table-responsive">
        <table
          v-if="invoiceGroups && invoiceGroups.length"
          class="table table-bordered table-hover table-striped w-100 dataTable dtr-inline"
        >
          <thead>
            <tr>
              <th>Numer faktury</th>
              <th>Data wystawienia</th>
              <th>NIP wystawcy</th>
              <th>ID wniosku</th>
              <th>Nazwa szkoły</th>
              <th>Imię i nazwisko ucznia</th>
              <th>Kwota faktury</th>
              <th>Kwota zakwalifikowana do refundacji z faktury (KORD)</th>
              <th>Do wykorzystania pozostało</th>
              <th>UID dokumentu MDOK</th>
              <th>Status faktury</th>
              <th>Uwagi</th>
              <th>&nbsp;</th>
            </tr>
          </thead>

          <tbody>
            <template v-for="(group, groupIndex) of invoiceGroups">
              <template v-if="group.invoices.length > 1">
                <tr :key="'group_' + groupIndex" :class="{ 'invoice-group-even': groupIndex % 2 }">
                  <td>{{ group.number }}</td>
                  <td>{{ formatDate(group.date) }}</td>
                  <td>{{ group.nip }}</td>
                  <td class="text-center">-</td>
                  <td class="text-center">-</td>
                  <td class="text-center">-</td>
                  <td class="text-right">{{ formatAmount(group.price) }}</td>
                  <td class="text-right">
                    <b>{{ formatAmount(group.priceToPay) }}</b>
                  </td>
                  <td class="text-right">{{ formatAmount(group.priceToPayRest) }}</td>
                  <td class="text-center">-</td>
                  <td class="text-center">-</td>
                  <td class="text-center">-</td>
                  <td>&nbsp;</td>
                </tr>

                <tr
                  v-for="(invoice, invoiceIndex) of group.invoices"
                  :key="'group_' + groupIndex + '_invoice_' + invoiceIndex"
                  :class="{ 'invoice-group-even': groupIndex % 2 }"
                >
                  <td class="text-center">-</td>
                  <td class="text-center">-</td>
                  <td class="text-center">-</td>
                  <td>{{ invoice.document.id }}</td>
                  <td>{{ invoice.document.school }}</td>
                  <td>{{ invoice.document.studentName }}</td>
                  <td class="text-center">-</td>
                  <td class="text-right">{{ formatAmount(invoice.priceToPay) }}</td>
                  <td class="text-center">-</td>
                  <td>{{ invoice.mdokUid }}</td>
                  <td class="text-center">{{ invoiceStatus(invoice) }}</td>
                  <td>{{ invoice.description }}</td>
                  <actions entity-type="invoice" :actions="actions" :object="invoice" @edit="editInvoice(invoice)" />
                </tr>
              </template>

              <tr v-else :key="'group_' + groupIndex" :class="{ 'invoice-group-even': groupIndex % 2 }">
                <td>{{ group.number }}</td>
                <td>{{ formatDate(group.date) }}</td>
                <td>{{ group.nip }}</td>
                <td>{{ group.invoices[0].document.id }}</td>
                <td>{{ group.invoices[0].document.school }}</td>
                <td>{{ group.invoices[0].document.studentName }}</td>
                <td class="text-right">{{ formatAmount(group.price) }}</td>
                <td class="text-right">
                  <b>{{ formatAmount(group.priceToPay) }}</b>
                </td>
                <td class="text-right">{{ formatAmount(group.priceToPayRest) }}</td>
                <td>{{ group.invoices[0].mdokUid }}</td>
                <td class="text-center">{{ invoiceStatus(group.invoices[0]) }}</td>
                <td>{{ group.invoices[0].description }}</td>
                <actions
                  entity-type="invoice"
                  :actions="actions"
                  :object="group.invoices[0]"
                  @edit="editInvoice(group.invoices[0])"
                />
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div slot="footer">
        <post-form text="Pobierz CSV" icon="fas fa-download mr-2" class="mr-3" :url="csvUrl" />
      </div>
    </panel>

    <invoice-modal
      :id="invoiceToEdit.idToEdit"
      :show="showModal"
      :max-to-pay="invoiceToEdit.maxToPay"
      :invoices="invoiceToEdit.invoices"
      @close="hideInvoice"
      @update="updateInvoice"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import InvoiceModal from '@/components/modals/InvoiceModal';
import Panel from '@/components/panel/Panel.vue';
import PostForm from '@/components/postForm/PostForm.vue';
import Actions from '@/components/table/components/Actions.vue';
import { InvoiceStatus } from '@/enums/invoiceStatus.enum';
import { TableName } from '@/enums/tableName';
import { date } from '@/filters/date';
import { formMixin } from '@/mixins/form';
import { notificationsMixin } from '@/mixins/notifications';
import { invoiceResource } from '@/resources/invoice.resource';
import { invoiceRepositoryFiltersSchema } from '@/schemas/invoiceRepositoryFilters.schema.ts';
import { config } from '@/utils/config';
import { EDIT_INVOICE_ACTION } from '@/utils/constants';
import { getInvoiceGroupsList, getInvoiceFilters } from '@/utils/invoices';
import { serverError } from '@/utils/serverError';
import { formatAmountFunc } from '@/utils/tables';

// @vuese
// Repozytorium faktur
// @group InvoiceseRepository
export default {
  components: { Actions, Panel, PostForm, InvoiceModal },
  mixins: [formMixin, notificationsMixin],

  schema: invoiceRepositoryFiltersSchema,

  data() {
    return {
      showModal: false,
      actions: [{ ...EDIT_INVOICE_ACTION, permissions: ['INVOICE.UPDATE'] }],
      invoiceToEdit: {
        idToEdit: undefined,
        invoice: undefined,
        document: undefined,
        invoices: [],
        maxToPay: 0,
        totalToPay: 0
      },
      model: getInvoiceFilters(this.$route)
    };
  },

  computed: {
    ...mapState({
      invoices: state => state.invoice.list
    }),
    invoiceGroups() {
      return this.invoices
        .reduce((groupList, invoice) => {
          let group = groupList.find(item => item.nip === invoice.nip && item.number === invoice.number);
          if (!group) {
            group = {
              nip: invoice.nip,
              number: invoice.number,
              date: invoice.date,
              price: invoice.price,
              priceToPay: 0,
              priceToPayRest: 0,
              invoices: []
            };
            groupList.push(group);
          }
          group.invoices.push(invoice);
          return groupList;
        }, [])
        .map(group => {
          group.priceToPay = group.invoices.reduce((sum, invoice) => sum + invoice.priceToPay, 0);
          group.priceToPayRest = group.price - group.priceToPay;
          return group;
        });
    },
    csvUrl() {
      const queryProperties = [];
      for (const key of Object.keys(this.$route.query)) {
        if (!!this.$route.query[key] || this.$route.query[key] === false || this.$route.query[key] === 0) {
          queryProperties.push(`${key}=${encodeURI(this.$route.query[key])}`);
        }
      }

      return config.csvURL + '/invoices/' + this.$route.params.procedureId + '?' + queryProperties.join('&');
    }
  },

  methods: {
    formatDate: date,

    // @vuese
    // Nakłada wybrane filtry
    applyFilters() {
      if (this.$schema.$validateAll().length) {
        this.notifyError(Errors.FORM_CORRECT_ERRORS);
        return;
      }
      this.$router.replace({ query: this.$schema.$getData() });
      this.updateList(this.$route);
    },

    // @vuese
    // Aktualizacja listy
    updateList(to) {
      return getInvoiceGroupsList(to).catch(err => this.notifyError(serverError(err)));
    },

    // @vuese
    // Aktualizacja listy po zmianie routingu
    beforeRouteUpdate(to, from, next) {
      return updateList(to, next).then(() => next());
    },

    // @vuese
    // Mapowanie wartości statusu faktury
    invoiceStatus(invoice) {
      return invoice.status === InvoiceStatus.APPROVED ? 'Z' : 'N';
      return invoice.status === InvoiceStatus.APPROVED ? 'Z' : 'N';
    },

    // @vuese
    // Formatowanie daty
    formatAmount: formatAmountFunc,

    // @vuese
    // Aktualizacja faktury
    updateInvoice(invoice) {
      invoice.entityType = TableName.document;
      invoice.entityId = this.invoiceToEdit.document.documentId.toString();

      return invoiceResource
        .update(this.invoiceToEdit.idToEdit, invoice)
        .then(() => this.hideInvoice())
        .then(() => this.updateList(this.$route));
    },

    // @vuese
    // Zamknięcie modala z edycją faktury
    hideInvoice() {
      this.invoiceToEdit.idToEdit = undefined;
      this.invoiceToEdit.invoice = undefined;
      this.invoiceToEdit.document = undefined;
      this.invoiceToEdit.invoices = [];
      this.invoiceToEdit.maxToPay = 0;
      this.invoiceToEdit.totalToPay = 0;
      this.showModal = false;
    },

    // @vuese
    // Pobranie danych potrzebnych do edycji faktury i wyświetlenie modala
    async editInvoice(invoice) {
      this.invoiceToEdit.idToEdit = invoice.id;
      this.invoiceToEdit.invoice = invoice;

      await this.$store.dispatch(`invoice/get`, invoice.id);
      const document = await this.$store.dispatch('documentIndex/get', invoice.document.id);
      this.invoiceToEdit.document = document;
      this.invoiceToEdit.invoices = (
        await invoiceResource.list({
          entityType: TableName.document,
          entityId: invoice.document.id.toString()
        })
      ).list;

      this.invoiceToEdit.totalToPay = document.decisionSecondYearTotal;
      const alreadyPaid = this.invoiceToEdit.invoices
        .filter(item => item.id !== invoice.id)
        .map(item => item.priceToPay)
        .reduce((total, invoiceToPay) => total + invoiceToPay, 0);

      this.invoiceToEdit.maxToPay = this.invoiceToEdit.totalToPay - alreadyPaid;
      this.showModal = true;

      return;
    }
  }
};
</script>

<style>
.invoices-repository .panel-toolbar {
  width: 100%;
  align-items: start;
  padding: 15px;
}

.invoices-repository .table-responsive tbody tr {
  background-color: #fdfdfd;
}

.invoices-repository .table-responsive tbody tr.invoice-group-even {
  background-color: rgba(80, 80, 80, 0.1);
}
</style>
